import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import toast from 'react-hot-toast';

interface Invoice {
  invoiceNumber: string;
  auctionId: string;
  auctionTitle: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  items: Array<{
    lotNumber: string;
    sku: string;
    title: string;
    quantity: number;
    highBid: number;
    picked?: boolean;
  }>;
  total: number;
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
  pickedUpAt?: string;
  pickupSignature?: string;
  pickupNotes?: string;
}

const PickupConfirmation: React.FC = () => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [notes, setNotes] = useState('');
  const [verifiedItems, setVerifiedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  useEffect(() => {
    // Setup canvas for signature
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2; // 2x for retina
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Set drawing styles
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const loadInvoice = async () => {
    if (!invoiceNumber) return;

    try {
      const docRef = doc(db, 'invoices', invoiceNumber);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as Invoice;
        setInvoice(data);
      } else {
        toast.error('Invoice not found');
        navigate('/picker');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const toggleItem = (sku: string) => {
    setVerifiedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const checkAllItems = () => {
    if (!invoice) return;
    setVerifiedItems(new Set(invoice.items.map(item => item.sku)));
  };

  const handleComplete = async () => {
    if (!invoice) return;

    // Validation
    if (!hasSignature) {
      toast.error('Customer signature is required');
      return;
    }

    setSaving(true);
    try {
      // Get signature as data URL
      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Canvas not found');
      
      const signatureDataURL = canvas.toDataURL('image/png');

      // Upload signature to Firebase Storage
      const signatureRef = ref(storage, `signatures/${invoice.invoiceNumber}/${Date.now()}.png`);
      await uploadString(signatureRef, signatureDataURL, 'data_url');
      const signatureURL = await getDownloadURL(signatureRef);

      // Update invoice
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        status: 'picked_up',
        pickStatus: 'ready_for_pickup',
        pickedUpAt: new Date().toISOString(),
        pickupSignature: signatureURL,
        ...(notes ? { pickupNotes: notes } : {}),
        items: invoice.items.map(item => ({
          ...item,
          pickedUp: verifiedItems.has(item.sku)
        })),
        updatedAt: new Date().toISOString()
      });

      toast.success('Pickup confirmed! Customer signature saved.');
      navigate(`/invoices/${invoice.invoiceNumber}`);
    } catch (error) {
      console.error('Error completing pickup:', error);
      toast.error('Failed to complete pickup');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <p className="text-gray-600">Invoice not found</p>
      </div>
    );
  }

  const allChecked = verifiedItems.size === invoice.items.length;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800 mb-2"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold">Pickup Confirmation</h1>
          <p className="text-gray-600">
            {invoice.bidder.lastName}, {invoice.bidder.firstName} - Bidder #{invoice.bidderNumber}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">
            ${(invoice.total ?? 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-600">
            {invoice.items.length} items
          </div>
        </div>
      </div>

      {/* Storage Location */}
      {invoice.storageLocation && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
          <div className="font-semibold text-yellow-900">📍 Storage Location:</div>
          <div className="text-xl font-bold text-yellow-900">{invoice.storageLocation}</div>
        </div>
      )}

      {/* Items Checklist */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Verify Items</h2>
          <div className="flex items-center gap-3">
            <span className={`text-lg font-semibold ${allChecked ? 'text-green-600' : 'text-orange-600'}`}>
              {verifiedItems.size} / {invoice.items.length} verified
            </span>
            {!allChecked && (
              <button
                onClick={checkAllItems}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 font-medium"
              >
                Check All Items
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {invoice.items.map((item) => {
            const isChecked = verifiedItems.has(item.sku);
            return (
              <div
                key={item.sku}
                onClick={() => toggleItem(item.sku)}
                className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition ${
                  isChecked
                    ? 'bg-green-50 border-green-500'
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                      isChecked
                        ? 'bg-green-500 border-green-500'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {isChecked && (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold">Lot {item.lotNumber} - {item.title}</div>
                    <div className="text-sm text-gray-600">SKU: {item.sku} • Qty: {item.quantity}</div>
                  </div>
                </div>
                <div className="font-bold text-gray-700">${(item.highBid ?? 0).toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Pickup Notes (Optional)</h2>
        <p className="text-sm text-gray-600 mb-3">
          Note any missing or damaged items, or other issues that need attention:
        </p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Box 3 was opened, Lot 45 missing one piece, customer noted damage to item..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
      </div>

      {/* Signature */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Customer Signature *</h2>
        <p className="text-sm text-gray-600 mb-3">
          Customer must sign to confirm receipt of all items:
        </p>
        
        <div className="border-2 border-gray-300 rounded-lg mb-3">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className="w-full h-48 cursor-crosshair touch-none"
            style={{ touchAction: 'none' }}
          />
        </div>

        <button
          onClick={clearSignature}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          Clear Signature
        </button>
      </div>

      {/* Complete Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <button
          onClick={handleComplete}
          disabled={saving || !hasSignature || !allChecked}
          className="w-full px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold text-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Processing...' : '✓ Complete Pickup'}
        </button>
        {!allChecked && (
          <p className="text-red-600 text-sm text-center mt-2">
            All items must be verified before completing pickup
          </p>
        )}
        {allChecked && !hasSignature && (
          <p className="text-red-600 text-sm text-center mt-2">
            Customer signature is required
          </p>
        )}
      </div>
    </div>
  );
};

export default PickupConfirmation;